import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Filter } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LABELS = {
  visit: "زيارة الصفحة",
  calc_start: "بدء الحاسبة (أول تعديل)",
  proposal_open: "فتح محرر عرض السعر",
  pdf_generated: "إنشاء ملف العرض",
  pdf_download: "طلب الطباعة / التنزيل",
  ai_request: "ضغط «احسب الآن»",
  ai_success: "ظهور السعر",
  ai_fail: "فشل الحساب",
  signup_open: "فتح نافذة التسجيل",
  signup_complete: "إكمال تسجيل جديد",
  login_complete: "تسجيل دخول",
  buy_open: "فتح نافذة الشراء",
  package_select: "اختيار باقة",
  checkout_start: "إنشاء جلسة دفع",
  pay_success: "دفع ناجح (تحقق الخادم)",
  pay_fail: "دفع فاشل / ملغى",
};

const GROUPS = [
  ["main", "المسار الأساسي — النسبة من الزوار"],
  ["ai", "الحساب بالذكاء الاصطناعي — النسبة من الطلبات"],
  ["signup", "التسجيل — النسبة من فتح النافذة"],
  ["purchase", "الشراء — النسبة من فتح النافذة"],
];

const CARD = "rounded-xl border border-[#2e3a5c] bg-[#1b2540]";
const stat = (d, g, id) => (d.groups[g] || []).find((r) => r.id === id)?.users ?? 0;
const pct = (n, base) => (base ? `${Math.round((n / base) * 100)}%` : "—");

function GroupTable({ id, title, rows }) {
  const base = rows[0]?.users || 0;
  return (
    <div data-testid={`funnel-group-${id}`} className={`${CARD} overflow-x-auto`}>
      <div className="px-3 py-2 text-xs font-bold text-[#18c98f] bg-[#182138]">{title}</div>
      <table className="w-full text-xs">
        <thead className="text-white/50 whitespace-nowrap">
          <tr><th className="text-start p-2">المرحلة</th><th className="p-2">مستخدمون</th><th className="p-2">جلسات</th><th className="p-2">أحداث</th><th className="p-2">%</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} data-testid={`funnel-step-${r.id}`} className="border-t border-[#2e3a5c]">
              <td className="p-2 text-white/90 whitespace-nowrap">{LABELS[r.id] || r.id}</td>
              <td className="p-2 text-center font-mono-num font-bold text-white">{r.users}</td>
              <td className="p-2 text-center font-mono-num text-white/70">{r.sessions}</td>
              <td className="p-2 text-center font-mono-num text-white/70">{r.events}</td>
              <td className="p-2 text-center font-mono-num text-[#18c98f] whitespace-nowrap">{pct(r.users, base)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Breakdown({ title, items, testId }) {
  return (
    <div data-testid={testId} className={`${CARD} p-3`}>
      <div className="text-xs font-bold text-white/80 mb-2">{title}</div>
      {items?.length ? items.map((it) => (
        <div key={it.key} className="flex justify-between text-xs py-0.5">
          <span className="text-white/70">{it.key}</span>
          <span className="font-mono-num text-white">{it.events} <span className="text-white/40">({it.users} مستخدم)</span></span>
        </div>
      )) : <div className="text-xs text-white/40">لا توجد بيانات</div>}
    </div>
  );
}

function Kpis({ data }) {
  const items = [
    ["زوار", stat(data, "main", "visit")],
    ["حصلوا على سعر", stat(data, "main", "ai_success")],
    ["نزّلوا عرض السعر", stat(data, "main", "pdf_download")],
    ["دفعوا", stat(data, "purchase", "pay_success")],
  ];
  return (
    <div data-testid="funnel-kpis" className="grid grid-cols-2 sm:grid-cols-4 gap-2">
      {items.map(([label, n]) => (
        <div key={label} className={`${CARD} p-3`}>
          <div className="text-[11px] text-white/60">{label}</div>
          <div className="mt-1 font-mono-num font-black text-2xl text-white">{n}</div>
        </div>
      ))}
    </div>
  );
}

function SourcesTable({ sources }) {
  return (
    <div data-testid="funnel-sources-table" className={`${CARD} overflow-x-auto`}>
      <table className="w-full text-xs">
        <thead className="text-white/50 whitespace-nowrap"><tr><th className="text-start p-2">المصدر</th><th className="p-2">زوار</th><th className="p-2">حصلوا على سعر</th><th className="p-2">نزّلوا العرض</th><th className="p-2">دفعوا</th></tr></thead>
        <tbody>
          {sources.map((s) => (
            <tr key={s.source} className="border-t border-[#2e3a5c] text-white/85">
              <td className="p-2">{s.source}</td>
              <td className="p-2 text-center font-mono-num">{s.visit}</td>
              <td className="p-2 text-center font-mono-num">{s.ai_success}</td>
              <td className="p-2 text-center font-mono-num">{s.pdf_download}</td>
              <td className="p-2 text-center font-mono-num">{s.pay_success}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminFunnel({ adminKey }) {
  const [days, setDays] = useState(7);
  const [includeTest, setIncludeTest] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!adminKey) return;
    setLoading(true);
    setFailed(false);
    axios
      .get(`${API}/admin/funnel`, { params: { days, include_test: includeTest }, headers: { "X-Admin-Key": adminKey } })
      .then((r) => setData(r.data))
      .catch(() => { setData(null); setFailed(true); })
      .finally(() => setLoading(false));
  }, [adminKey, days, includeTest]);

  return (
    <section data-testid="admin-funnel-section" className="rounded-2xl bg-[#1f2a47] border border-[#2e3a5c] p-4 sm:p-5 space-y-4 font-arabic text-white" dir="rtl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-black text-lg text-white flex items-center gap-2">
          <Filter className="h-5 w-5 text-[#18c98f]" /> مسار التحويل
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-[11px] text-white/70 inline-flex items-center gap-1 cursor-pointer">
            <input data-testid="funnel-include-test" type="checkbox" className="accent-[#18c98f]" checked={includeTest} onChange={(e) => setIncludeTest(e.target.checked)} />
            تضمين بيئة الاختبار
          </label>
          {[1, 7, 30].map((d) => (
            <button key={d} data-testid={`funnel-range-${d}`} onClick={() => setDays(d)}
              className={`h-8 px-3 rounded-full text-xs font-bold transition-colors ${days === d ? "bg-[#18c98f] text-white" : "bg-[#1b2540] border border-[#2e3a5c] text-white/80 hover:text-white"}`}>
              {d === 1 ? "اليوم" : `${d} يوم`}
            </button>
          ))}
        </div>
      </div>

      {loading && <Loader2 className="h-5 w-5 animate-spin text-[#18c98f]" />}
      {failed && !loading && <div data-testid="funnel-error" className="text-xs text-[#f97316]">تعذّر تحميل البيانات — اضغط «تحديث».</div>}
      {data && (
        <>
          <Kpis data={data} />
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
            {GROUPS.map(([id, title]) => <GroupTable key={id} id={id} title={title} rows={data.groups[id] || []} />)}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Breakdown testId="funnel-buy-from" title="من أين فُتحت نافذة الشراء" items={data.buy_open_from} />
            <Breakdown testId="funnel-packages" title="الباقات المختارة" items={data.packages_selected} />
            <Breakdown testId="funnel-ai-fails" title="أسباب فشل الحساب" items={data.ai_fail_codes} />
            <Breakdown testId="funnel-pay-fails" title="أسباب فشل الدفع" items={data.pay_fail_codes} />
            <div data-testid="funnel-ai-latency" className={`${CARD} p-3 text-xs text-white/80`}>
              <div className="font-bold mb-2">زمن استجابة الحساب</div>
              {data.ai_latency
                ? <>متوسط <b className="font-mono-num text-white">{(data.ai_latency.avg_ms / 1000).toFixed(1)}ث</b> · أقصى <b className="font-mono-num text-white">{(data.ai_latency.max_ms / 1000).toFixed(1)}ث</b></>
                : <span className="text-white/40">لا توجد بيانات</span>}
            </div>
          </div>
          {data.sources?.length > 0 && <SourcesTable sources={data.sources} />}
          <p className="text-[11px] text-white/50 leading-relaxed">
            طريقة الاحتساب: «مستخدم» = متصفح فريد، «جلسة» = تبويب أو زيارة واحدة، «أحداث» = إجمالي المرات. النسبة = مستخدمو المرحلة ÷ مستخدمي أول مرحلة في المجموعة. نافذة الشراء يمكن فتحها من أي مكان، لذلك تُعرض كمسار جانبي مع مصدر الفتح. «طلب التنزيل» يعني فتح نافذة الطباعة فقط، ولا يثبت إرسال العرض للعميل أو توقيعه. تُستبعد بيئة الاختبار ومتصفحات الأدمن افتراضيًا، ولا تُرسل أي أسماء أو أرقام أو نصوص مشاريع إلى التحليلات.
          </p>
        </>
      )}
    </section>
  );
}
